import type {
  LocationFieldList,
  Int64Int64Tuple,
  ISearchResultItem,
  LibraryItem
} from '@/@types/client-api';
import { LiveProgramWebcastVariation } from '@/@types/content';
import { DateType } from '@/components/ui/DateTag';
import { LiveSegment } from '@/hooks/PDP/useProgramCatalogRelations';

export const STARTTIMEOFFSET = 30;
export const ENDTIMEOFFSET = 5;

export const isLocationOnAir = (l: LocationFieldList, d: Date = new Date(Date.now())) => {
  return l.secondsOnAir ? isInTimeList(l.secondsOnAir, d.toUTCString()) : l.isOnAir;
};

export const isLiveProgramOnAir = (v: LiveProgramWebcastVariation) => {
  if (v.eventStartDate && v.eventEndDate) {
    return isStringyDateOnAir(v.eventStartDate, v.eventEndDate);
  }
  return false;
};

export const getFormattedCreditCardExpirationDate = (month?: number, year?: number) => {
  return `${String(month).padStart(2, '0')}/${year}`;
};

export const isDateOnAir = (
  startDate: Date,
  endDate: Date,
  liveDate: Date = new Date(Date.now())
) => {
  const start = new Date(startDate.getTime() - STARTTIMEOFFSET * 60000);
  const end = new Date(endDate.getTime() + ENDTIMEOFFSET * 60000);
  return liveDate < end && start <= liveDate;
};
export const isStringyDateOnAir = (
  startTimeUtc: string | null | undefined,
  endTimeUtc: string | null | undefined,
  liveDate: string = new Date(Date.now()).toUTCString()
) => {
  if (startTimeUtc && endTimeUtc) {
    return isDateOnAir(new Date(startTimeUtc), new Date(endTimeUtc), new Date(liveDate));
  }
  return false;
};

export const isInTimeList = (
  times: Array<Int64Int64Tuple> | null | undefined,
  d: string = new Date(Date.now()).toUTCString()
) => {
  const utcDate = new Date(d);
  const secs = utcDate.getTime() / 1000;

  return (
    times?.some(s => (s?.item1 ?? 0) <= secs && (s?.item2 ?? Number.MAX_SAFE_INTEGER) >= secs) ??
    false
  );
};

export const isSearchItemOnAir = (
  s: Omit<ISearchResultItem & LibraryItem, 'searchableType' | 'locations' | 'formats'>,
  liveDate: string = new Date(Date.now()).toUTCString()
) => {
  if (s.secondsOnAir) {
    return isInTimeList(s.secondsOnAir, liveDate);
  } else {
    return isStringyDateOnAir(s.startTimeUtc, s.endTimeUtc, liveDate);
  }
};

export const isSegmentCurrentlyLive = (s: LiveSegment, dateMillis: number) => {
  const startTime = new Date(s.start);
  const endTime = new Date(s.end);
  const liveDate = new Date(dateMillis);
  return isDateOnAir(startTime, endTime, liveDate);
};
type LiveStatus = 'Upcoming Live' | 'Live' | 'Live ended' | undefined;
export const determineLiveProgramStatus = (
  eventStartDate?: string,
  eventEndDate?: string,
  segments?: LiveSegment[]
): LiveStatus => {
  if (window?.location?.search?.includes('forceDarkMode=true') ?? false) {
    return 'Live';
  }
  if (!eventStartDate || !eventEndDate) return undefined;
  const start = eventStartDate ? new Date(eventStartDate) : undefined;
  const end = eventEndDate ? new Date(eventEndDate) : undefined;
  if (!start || !end) return undefined;
  const now = new Date().getTime();
  const bufferedStart = start.getTime() - STARTTIMEOFFSET * 60 * 1000;
  const bufferedEnd = end.getTime() + ENDTIMEOFFSET * 60 * 1000;
  if (now < bufferedStart) {
    return 'Upcoming Live';
  } else if (now > bufferedEnd) {
    return 'Live ended';
  } else {
    // Only show "Live" when within 30 minutes of start / 5 minutes end of some segment
    if (segments?.find(s => isSegmentCurrentlyLive(s, now))) return 'Live';

    // If there is no ongoing schedule segment, we are in between days and show this state
    return 'Upcoming Live';
  }
};

export const isExpirationWithinDayRange = (
  date?: string | null,
  daysBeforeExpiration?: number,
  daysAfterExpiration?: number
) => {
  if (!date) return false;
  const utcDate = new Date(date);

  const now = new Date();
  const diffInMillis = utcDate.getTime() - now.getTime();
  const diffInDays = diffInMillis / (1000 * 60 * 60 * 24);

  if (daysBeforeExpiration && daysAfterExpiration) {
    return diffInDays >= -daysAfterExpiration && diffInDays <= daysBeforeExpiration;
  } else if (daysBeforeExpiration) {
    return diffInDays <= daysBeforeExpiration && diffInDays >= 0;
  } else if (daysAfterExpiration) {
    return diffInDays >= -daysAfterExpiration && diffInDays <= 0;
  }
  return false;
};

export const isDateInPast = (date?: string | null) => {
  if (!date) return false;
  const utcDate = new Date(date);
  const now = new Date();
  if (utcDate.getTime() < now.getTime()) {
    return true;
  }
  return false;
};

export const parseLiveProgramDate = (
  dateString: string | undefined | null
): DateType | undefined => {
  if (!dateString) return undefined;

  const regex = /^(\w+)(?: (\d+(?: – \d+)?)(?:,))? (\d{4})$/;
  const match = dateString.match(regex);

  if (!match) return undefined;

  const [, month, day] = match;
  const dayPart = day ? ` ${day}` : '';

  return {
    month,
    day: `${dayPart}`
  };
};
